import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  getNotifications,
  markNotificationRead,
  clearNotificationBadge,
  notiHrefToPath,
  type NotiAction,
  type RemoteNoti,
} from "../../api/notifications";
import { performInvitationAction } from "../../api/followerInvitations";
import { useErrorToastStore } from "../../store/errorToastStore";
import { getApiErrorMessage } from "../../lib/apiError";
import NotificationItem from "./NotificationItem";

const PAGE_SIZE = 10;
const QUERY_KEY = ["notifications"] as const;

interface Props {
  /** The heading: a plain <h2> on the page, the dialog title in the bell panel. */
  title: ReactNode;
  /** Called after a notification with a link was opened (the bell panel closes itself). */
  onNavigate?(): void;
  /** Keeps the header's right edge clear for the panel's close button. */
  reserveCloseSpace?: boolean;
}

/** The notification list, shared by the /notifications page and the bell panel. */
export default function NotificationsList({
  title,
  onNavigate,
  reserveCloseSpace = false,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [done, setDone] = useState<Record<number, string>>({});

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: QUERY_KEY,
      queryFn: ({ pageParam }) => getNotifications(pageParam, PAGE_SIZE),
      initialPageParam: 0,
      getNextPageParam: (last, pages) =>
        last.length === PAGE_SIZE ? pages.length : undefined,
    });

  // Android dedups pages by id (EasyHashPagination keyed on id); a later page wins.
  const notis = useMemo(() => {
    const byId = new Map<number, RemoteNoti>();
    data?.pages.flat().forEach((n) => byId.set(n.id, n));
    return [...byId.values()];
  }, [data]);

  // Android clears the whole badge server-side whenever this screen opens
  useEffect(() => {
    clearNotificationBadge()
      .catch(() => {})
      .finally(() =>
        qc.invalidateQueries({ queryKey: ["notificationsBadge"] }),
      );
  }, [qc]);

  const { mutate: readNoti } = useMutation({
    mutationFn: (id: number) => markNotificationRead(id),
    onMutate: (id) => {
      qc.setQueryData<InfiniteData<RemoteNoti[], number>>(QUERY_KEY, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) =>
                page.map((n) =>
                  n.id === id ? { ...n, status: "READ" as const } : n,
                ),
              ),
            }
          : old,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notificationsBadge"] });
    },
  });

  function handleClick(noti: RemoteNoti) {
    if (noti.status !== "READ") readNoti(noti.id);
    const path = notiHrefToPath(noti.href);
    if (!path) return;
    navigate(path);
    onNavigate?.();
  }

  async function handleAction(noti: RemoteNoti, action: NotiAction) {
    if (noti.status !== "READ") readNoti(noti.id);
    setBusyId(noti.id);
    try {
      const { code, joinedConvId } = await performInvitationAction(noti, action);
      if (joinedConvId != null) {
        navigate(`/street-chat?conv=${joinedConvId}`);
        onNavigate?.();
      } else {
        setDone((d) => ({
          ...d,
          [noti.id]:
            code === "forward"
              ? t("msg_sent_invitation_success")
              : t("done"),
        }));
      }
    } catch (err) {
      useErrorToastStore
        .getState()
        .showError(getApiErrorMessage(err, t("msg_invitation_is_not_available")));
    } finally {
      setBusyId(null);
    }
  }

  // Back to the first page only.
  function handleRefresh() {
    qc.resetQueries({ queryKey: QUERY_KEY });
  }

  const muted = {
    padding: 32,
    textAlign: "center" as const,
    color: "var(--foreground-subtle)",
  };

  return (
    <div style={{ background: "var(--bg-base)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          paddingRight: reserveCloseSpace ? 48 : 16,
          borderBottom: "1px solid var(--border)",
        }}
      >
        {title}
        <button
          type="button"
          onClick={handleRefresh}
          style={{
            marginLeft: "auto",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--foreground-subtle)",
            fontSize: 13,
          }}
        >
          {t("notifications_refresh")}
        </button>
      </div>

      {isLoading && notis.length === 0 ? (
        <div data-testid="loading-indicator" style={muted}>
          {t("notifications_loading")}
        </div>
      ) : notis.length === 0 ? (
        <div data-testid="empty-no-notifications" style={muted}>
          {t("notifications_empty")}
        </div>
      ) : (
        <>
          {notis.map((noti) => (
            <NotificationItem
              key={noti.id}
              noti={noti}
              onClick={handleClick}
              onAction={handleAction}
              busy={busyId === noti.id}
              doneLabel={done[noti.id] ?? null}
            />
          ))}
          {hasNextPage && (
            <button
              type="button"
              data-testid="load-more-button"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
              style={{
                width: "100%",
                padding: 12,
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--primary-light)",
                fontSize: 14,
              }}
            >
              {t("notifications_load_more")}
            </button>
          )}
        </>
      )}
    </div>
  );
}
